import { ChangeDetectionStrategy, Component, effect, ElementRef, EventEmitter, Input, Output, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AiUtility } from 'countable@helpers';
import { ChatActionModel, ChatListModel, ChatsModel, LucaEngListModel } from 'countable@model';
import { AiService } from 'countable@services';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators';
import { videoModel, VIDEOS } from 'src/app/model/ai/ai-getting-started-videos.model';
import { AiWorkingStepperInfoComponent } from '../ai-working-stepper-info/ai-working-stepper-info.component';

@Component({
  selector: 'ai-side-bar',
  templateUrl: './ai-side-bar.component.html',
  styleUrl: './ai-side-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiSideBarComponent {
  @ViewChild('gettingStartedVideo') gettingStartedVideo: ElementRef;
  @ViewChild('fileUploader') fileUploader: ElementRef;

  @Input() set chats(chats: ChatsModel[]) {
    if (chats && chats?.length > 0) {
      this.updateChatsList(structuredClone(chats));
    } else {
      this.originalChats.set([]);
      this.chatList.set([]);
    }
  }

  @Input() set updateChat(chat: ChatsModel) {
    if (chat?.id > 0 && chat?.filePath) {
      this.updateChatInformation(structuredClone(chat));
    }
  }

  @Input() set hasEngagements(hasNoEng: boolean) {
    this.hasNoEngagements.set(hasNoEng);
  }

  @Input() set selectedEng(eng: LucaEngListModel) {
    this.activeEng.set(structuredClone(eng));
  }

  @Input() set activeChatId(id: number) {
    this.currentChatId.set(id);
  }

  @Output() triggerAction: EventEmitter<Partial<ChatActionModel>> = new EventEmitter<Partial<ChatActionModel>>(true);
  @Output() chatListCount: EventEmitter<number> = new EventEmitter<number>(null);

  @ViewChild('deleteChat') deleteChat: TemplateRef<any>;
  @ViewChild('feedbackDialog') feedbackDialog: TemplateRef<any>;
  @ViewChild('aboutContext') aboutContext: TemplateRef<any>;
  @ViewChild('submitBugSuccess') submitBugSuccess: TemplateRef<any>;
  @ViewChild('submitFeedbackSuccess') submitFeedbackSuccess: TemplateRef<any>;
  @ViewChild('gettingStarted') gettingStarted: TemplateRef<any>;
  public aiUtil: typeof AiUtility = AiUtility;
  public description: FormControl<string | null> = new FormControl<string | null>('', [Validators.required]);
  public searchHistory: FormControl<string | null> = new FormControl<string | null>('');
  public originalChats: WritableSignal<ChatsModel[]> = signal<ChatsModel[]>([]);
  public chatList: WritableSignal<ChatListModel[]> = signal<ChatListModel[]>([]);
  public hasNoEngagements: WritableSignal<boolean> = signal<boolean>(false);
  public renameObj: WritableSignal<ChatsModel> = signal<ChatsModel>(null);
  public renameCtrl: FormControl<string | null> = new FormControl<string | null>('', [Validators.required]);
  public currentChatId: WritableSignal<number> = signal<number>(null);
  public activeEng: WritableSignal<LucaEngListModel> = signal<LucaEngListModel>(null);
  public hasNoSearchChat: WritableSignal<boolean> = signal<boolean>(false);
  public selectedFile: WritableSignal<File[] | null> = signal<File[] | null>(null);
  public feedBackCtrl: FormControl<'FEEDBACK' | 'BUG'> = new FormControl<'FEEDBACK' | 'BUG'>('FEEDBACK');
  public isSubmitInProgress: WritableSignal<boolean> = signal<boolean>(false);
  public placeHolderText: WritableSignal<string> = signal<string>(`e.g. Describe the issue you encountered or simply share your thoughts.
  Please include steps to reproduce it, and any relevant details that could help us resolve the problem.`);
  public gettingStartedWithLuca: videoModel[] = VIDEOS;
  public videoUrl: WritableSignal<videoModel> = signal<videoModel>(null);

  constructor(private dialog: MatDialog, private aiService: AiService) {

    this.searchHistory.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(500)
    ).subscribe({
      next: res => {
        if (!!res) {
          this.searchInChatsList(res);
        } else {
          this.hasNoSearchChat.set(false);
          this.updateChatsList(structuredClone(this.originalChats()));
        }
      }
    });

    this.chatList.set(this.aiUtil?.convertMapToArray(this.aiUtil?.emptyChatByDate(), null));

    effect(() => {
      let count = 0;
      this.chatList().forEach(chat => {
        count += chat.displayList.length;
      });
      this.chatListCount.emit(count);
    });
  }

  private updateChatsList(chatsList: ChatsModel[], captureState: boolean = false) {
    if (chatsList && chatsList?.length > 0) {
      const chatsWithConvertedTime = chatsList?.map(chat => {
        chat.titleWithDateTime = this.aiService.getFormattedCreatedAt(chat?.createdAt, chat?.title);
        return chat;
      });
      this.originalChats.set([...chatsWithConvertedTime]);
      const categorizedMap = this.aiUtil.categorizeDataToMap([...chatsWithConvertedTime]);
      const updatedChats = this.aiUtil?.convertMapToArray(categorizedMap, this.chatList());
      if (captureState) {
        const chatState = structuredClone(this.chatList());
        updatedChats?.forEach(c => {
          chatState?.forEach(s => {
            if (c?.displayKey === s?.displayKey) {
              c.isExpanded = s?.isExpanded;
            }
          });
        });
      }
      this.chatList.set(structuredClone(updatedChats));
    }
  }

  public renameHistory(chat: ChatsModel) {
    this.renameObj.set(structuredClone(chat));
    this.renameCtrl.setValue(this.renameObj()?.title, { emitEvent: false });
  }

  public confirmRename() {
    const chat = structuredClone(this.renameObj());
    chat.title = this.renameCtrl.getRawValue();
    this.renameObj.set(null);
    const currentChatList = this.chatList();
    this.originalChats.update(chats => {
      chats?.forEach(c => {
        if (c?.id === chat?.id) {
          c.title = chat?.title;
        }
      });
      return chats;
    });
    this.chatList.update(chats => {
      chats?.forEach(c => {
        const foundChat = c.displayList.find(l => l.id === chat.id);
        if (foundChat) {
          foundChat.title = chat.title;
        }
        const originalChat = currentChatList.find(o => o.id === c.id);
        if (originalChat) {
          c.isExpanded = originalChat.isExpanded;
        }
      });
      return chats;
    });
    this.triggerAction.emit({ actionType: 'RENAME_CHAT', chat: structuredClone(chat), performAction: true });
    this.updateChatsList(structuredClone(this.originalChats()), true);
  }

  public cancelRename() {
    this.renameObj.set(null);
    this.renameCtrl.reset();
  }

  private searchInChatsList(searchText: string = null) {
    const searchList = this.originalChats()?.filter(s => s?.title?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()));
    this.hasNoSearchChat.set(!(searchList && searchList?.length > 0));
    const categorizedMap = this.aiUtil.categorizeDataToMap(structuredClone(searchList));
    const listOfChats = this.aiUtil?.convertMapToArray(categorizedMap, null);
    listOfChats?.forEach(l => {
      l.isExpanded = (l?.displayList?.length > 0);
    });
    this.chatList.set(structuredClone(listOfChats));
  }

  public navigateToHistory(chat: ChatsModel) {
    this.triggerAction.emit({ actionType: 'SELECT_CHAT', chat: structuredClone(chat), performAction: true });
  }

  public deleteChatHistory(chat: ChatsModel) {
    this.dialog.open(this.deleteChat).afterClosed().subscribe({
      next: res => {
        if (res) {
          this.triggerAction.emit({
            actionType: 'DELETE_CHAT', chat: structuredClone(chat),
            performAction: true, isActiveSession: (chat?.id === this.currentChatId())
          });
          let originalChats = structuredClone(this.originalChats());
          originalChats = originalChats?.filter(c => c?.id !== chat?.id);
          this.originalChats.set(structuredClone(originalChats));
          this.updateChatsList(structuredClone(originalChats));
        }
      }
    });
  }

  private updateChatInformation(chat: ChatsModel) {
    if (chat?.id && chat?.filePath) {
      chat.titleWithDateTime = this.aiService.getFormattedCreatedAt(chat?.createdAt, chat?.title);
      const originalChats = structuredClone(this.originalChats());
      if (!(originalChats?.some(existingChat => existingChat.id === chat.id))) {
        const updatedList = originalChats?.length > 0 ? [...originalChats, chat] : [chat];
        this.originalChats.set(updatedList);
        this.updateChatsList(updatedList);
      }
    }
  }

  public openHowItWorks() {
    this.dialog.open(AiWorkingStepperInfoComponent);
  }

  public openFeedbackDialog() {
    this.selectedFile.set(null);
    this.isSubmitInProgress.set(false);
    this.feedBackCtrl.setValue('FEEDBACK');
    this.description.reset();
    this.dialog.open(this.feedbackDialog, {
      disableClose: true,
      id: 'feedback'
    })
  }

  submitFeedback() {
    const formData = new FormData();
    (this.activeEng()?.engId > 0) && formData.append('engagementId', this.activeEng()?.engId?.toString());
    (this.activeEng()?.engId > 0) && formData.append('engagementName', this.activeEng()?.engName);
    formData.append('type', this.feedBackCtrl?.value); // Assuming type is a string. Adjust if necessary.
    formData.append('description', this.description.getRawValue());
    this.selectedFile()?.forEach(f => {
      formData.append('files', f, f.name);
    });
    this.isSubmitInProgress.set(true);
    this.aiService.saveFeedback(formData).subscribe({
      next: res => {
        if (res?.status === 200 && res?.data) {
          this.dialog.getDialogById('feedback')?.close();
          this.openSuccessDialog(this.feedBackCtrl?.value);
        }
      }
    });
  }

  private openSuccessDialog(type: 'FEEDBACK' | 'BUG' = 'FEEDBACK') {
    type === 'FEEDBACK' && this.dialog.open(this.submitFeedbackSuccess);
    type === 'BUG' && this.dialog.open(this.submitBugSuccess);
  }

  public getStarted() {
    this.dialog.open(this.gettingStarted);
    this.videoUrl.set(VIDEOS[0]);
  }

  public onFileSelected(event: any, type: 'DND' | 'MANUAL') {
    let files: any[] = type === 'DND' ? event : event?.target?.files;
    let selectedFiles: File[] = [];
    const previouslySelectedFiles = this.selectedFile() || [];
    if (files && files.length > 0) {
      selectedFiles = Array.from(files);
    }
    const allSelectedFiles = [...previouslySelectedFiles, ...selectedFiles];
    this.selectedFile.set(allSelectedFiles);
    this.fileUploader.nativeElement.value = '';
  }

  public removeFile(i: number) {
    this.selectedFile.update((f) => {
      return f?.filter((file, index) => index !== i);
    });
  }

  public resetLink(video: videoModel) {
    this.videoUrl.set(video);
    this.gettingStartedVideo.nativeElement.currentTime = 0;
    this.gettingStartedVideo.nativeElement.load();
  }

}
